// Form di raccolta identità a fine chat (timing: 'at_end').
// Renderizzato da ChatPage quando completed=true e flow.settings.ask_personal_info.timing==='at_end'.
// Submit chiama POST /api/session/:id/identity. Skip salta senza POST.
// Su 409 IDENTITY_ALREADY_COLLECTED tratta come successo silenzioso.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RuleBicolor } from '@audiq/ui';
import type { IdentityField } from '@audiq/shared';
import { ApiError, submitIdentityAtEnd } from '../lib/api';

interface Props {
  sessionId: string;
  fields: ReadonlyArray<IdentityField>;
  required: boolean;
  onDone: () => void;
}

export function IdentityAtEndForm({ sessionId, fields, required, onDone }: Props) {
  const { t } = useTranslation();
  const [identity, setIdentity] = useState<Partial<Record<IdentityField, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const cleaned = Object.fromEntries(
      Object.entries(identity).filter(([, v]) => v && v.trim()),
    ) as Partial<Record<IdentityField, string>>;
    if (Object.keys(cleaned).length === 0) {
      // Niente da inviare → comportamento "salta".
      onDone();
      return;
    }
    setSubmitting(true);
    try {
      await submitIdentityAtEnd(sessionId, cleaned);
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'IDENTITY_ALREADY_COLLECTED') {
        // Successo silenzioso: lo stato finale è quello desiderato.
        onDone();
        return;
      }
      setError(t('chat.identityAtEnd.error_generic'));
    } finally {
      setSubmitting(false);
    }
  }

  function onSkip() {
    onDone();
  }

  return (
    <section
      className="mx-auto mt-6 w-full max-w-md rounded-md border p-5"
      style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
    >
      <h2 className="serif text-xl" style={{ color: 'var(--ink)' }}>
        {t('chat.identityAtEnd.title')}
      </h2>
      <p className="mt-1 text-sm" style={{ color: 'var(--mute)' }}>
        {t('chat.identityAtEnd.subtitle')}
      </p>
      <RuleBicolor className="my-4" />

      <form onSubmit={onSubmit} className="space-y-4">
        {fields.map((field) => (
          <div key={field}>
            <label htmlFor={`iaf-${field}`} className="mb-1 block text-sm font-medium">
              {t(`preChat.fields.${field}`)}
              {required && <span style={{ color: 'var(--danger)' }}> *</span>}
            </label>
            <input
              id={`iaf-${field}`}
              type={
                field === 'date_of_birth'
                  ? 'date'
                  : field === 'email'
                    ? 'email'
                    : field === 'phone'
                      ? 'tel'
                      : 'text'
              }
              value={identity[field] ?? ''}
              onChange={(e) => setIdentity((prev) => ({ ...prev, [field]: e.target.value }))}
              required={required}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ background: 'var(--surface)', borderColor: 'var(--line)' }}
              autoComplete={
                field === 'first_name'
                  ? 'given-name'
                  : field === 'last_name'
                    ? 'family-name'
                    : field === 'phone'
                      ? 'tel'
                      : field === 'email'
                        ? 'email'
                        : 'off'
              }
            />
          </div>
        ))}

        {error && (
          <p className="text-sm" role="alert" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            {submitting ? t('common.loading') : t('chat.identityAtEnd.submit')}
          </button>
          <button
            type="button"
            onClick={onSkip}
            disabled={submitting}
            className="rounded-md border px-4 py-2.5 text-sm font-medium disabled:opacity-50"
            style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
          >
            {t('chat.identityAtEnd.skip')}
          </button>
        </div>
      </form>
    </section>
  );
}
